<template>
  <v-card variant="outlined" class="pa-3">
    <h4 class="text-subtitle-2 mb-2">{{ $t("apiManager.history") }}</h4>
    <v-tooltip :disabled="!isRequestDisabled" location="top">
      <template #activator="{ props }">
        <span v-bind="props" class="d-flex align-center">
          <v-btn
            block
            color="success"
            class="mb-2"
            size="small"
            :class="{ 'rtl-btn': $i18n.locale === 'he' }"
            @click="requestHistoryHandler"
            :loading="loadingHistory"
            :disabled="isRequestDisabled"
          >
            <v-icon class="mr-2">mdi-history</v-icon>
            {{ $t("apiManager.requestHistory") }}
          </v-btn>
        </span>
      </template>
      <span :dir="$i18n.locale === 'he' ? 'rtl' : 'ltr'">{{
        requestError
      }}</span>
    </v-tooltip>
    <div class="d-flex align-center">
      <DateTimePicker v-model="from" :label="$t('apiManager.fromDate')" />
      <DateTimePicker v-model="to" :label="$t('apiManager.toDate')" />
    </div>
    <div class="d-flex align-center">
      <v-select
        v-model="selectedLayerTypes"
        :items="localizedLayerTypeOptions"
        :label="$t('apiManager.layerTypes')"
        multiple
        dense
        outlined
        hide-details
        class="pa-2"
        :class="{ 'rtl-input': $i18n.locale === 'he' }"
        :menu-props="{
          location: $i18n.locale === 'he' ? 'bottom right' : 'bottom left'
        }"
      />
      <v-select
        v-model="selectedCategory"
        :items="categoryOptions"
        :label="$t('apiManager.category')"
        dense
        outlined
        hide-details
        class="pa-2"
        :class="{ 'rtl-input': $i18n.locale === 'he' }"
        :menu-props="{
          location: $i18n.locale === 'he' ? 'bottom right' : 'bottom left'
        }"
      />
      <v-select
        v-model="selectedEvent"
        :items="eventOptions"
        :label="$t('apiManager.event')"
        dense
        outlined
        hide-details
        class="pa-2"
        :class="{ 'rtl-input': $i18n.locale === 'he' }"
        :menu-props="{
          location: $i18n.locale === 'he' ? 'bottom right' : 'bottom left'
        }"
      />
    </div>
    <v-alert
      v-if="historyRequestId"
      type="info"
      variant="tonal"
      class="mt-2 response-window"
      :class="{ 'rtl-alert': $i18n.locale === 'he' }"
    >
      <strong>{{ $t("apiManager.requestId") }}:</strong>
      <code class="request-id">{{ ' ' + historyRequestId }}</code>
      <div class="mt-1 text-caption">
        {{ $t("apiManager.requestIdDescription") }}
      </div>
      <div class="mt-1 text-caption text-primary">
        <v-icon size="small" class="mr-1">mdi-information</v-icon>
        {{ $t("apiManager.switchedToHistoryTab") }}
      </div>
    </v-alert>
    <!-- Response Display (can be slotted or passed as prop) -->
    <slot name="response"></slot>
  </v-card>
</template>

<script>
import DateTimePicker from "./DateTimePicker.vue";
import {
  getDefaultThreatTypes,
  THREAT_CATEGORIES,
  THREAT_EVENTS,
} from "../../config/threatTypes.js";
export default {
  name: "HistoryManager",
  components: { DateTimePicker },
  props: {
    loadingHistory: Boolean,
    historyRequestId: String,
    response: Object,
  },
  data() {
    const now = new Date();
    const threeHoursAgo = new Date(now.getTime() - 3 * 60 * 60 * 1000);
    return {
      from: new Date(threeHoursAgo).toISOString(),
      to: new Date(now).toISOString(),
      selectedLayerTypes: ["ALL_IL"],
      selectedCategory: null,
      selectedEvent: null,
    };
  },
  computed: {
    localizedLayerTypeOptions() {
      const layerTypes = ["ALL_IL", "CITIES", "SQUARES"];
      return layerTypes.map((type) => ({
        title: this.$t(`${type}`),
        value: type,
      }));
    },
    categoryOptions() {
      return THREAT_CATEGORIES.map((cat) => ({ title: cat, value: cat }));
    },
    eventOptions() {
      return THREAT_EVENTS.map((ev) => ({ title: ev, value: ev }));
    },
    isRequestDisabled() {
      // Must have both from and to, and both must be valid dates
      if (!this.from || !this.to) return true;
      const fromDate = new Date(this.from);
      const toDate = new Date(this.to);
      if (isNaN(fromDate) || isNaN(toDate)) return true;
      if (fromDate > toDate) return true;
      // Only category or only event is set (require both or neither)
      if (
        (this.selectedCategory && !this.selectedEvent) ||
        (!this.selectedCategory && this.selectedEvent)
      )
        return true;
      return false;
    },
    requestError() {
      if (!this.from || !this.to)
        return this.$t("apiManager.errorMissingDates");
      const fromDate = new Date(this.from);
      const toDate = new Date(this.to);
      if (isNaN(fromDate) || isNaN(toDate))
        return this.$t("apiManager.errorInvalidDates");
      if (fromDate > toDate) return this.$t("apiManager.errorFromAfterTo");
      if (
        (this.selectedCategory && !this.selectedEvent) ||
        (!this.selectedCategory && this.selectedEvent)
      )
        return this.$t("apiManager.errorCategoryEvent");
      return "";
    },
  },
  methods: {
    requestHistoryHandler() {
      this.$emit("request-history", {
        from: this.from,
        to: this.to,
        layerTypes: this.selectedLayerTypes,
        category: this.selectedCategory,
        event: this.selectedEvent,
      });
    },
    buildIso(date, time) {
      if (!date) return null;
      if (!time) {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0).toISOString();
      }
      const [hours, minutes] = time.split(":");
      const dt = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        Number(hours),
        Number(minutes),
        0
      );
      return dt.toISOString();
    }
  },
};
</script>

<style scoped>
@media (max-width: 768px) {
  .v-card {
    width: 100vw !important;
    max-width: 100vw !important;
    padding: 0.7em 0.2em !important;
  }
  .d-flex {
    flex-direction: column !important;
    gap: 0.7em !important;
  }
  .pa-2 {
    padding: 0.3em 0.1em !important;
  }
}
</style>
